import { useCallback, useEffect } from 'react';
import { useForm } from 'react-final-form';

import { type TranslatedComponent, translation } from '@hh.ru/front-static-app';
import { useCaptcha } from '@hh.ru/hhcaptcha';
import { VSpacingContainer, Title, Button } from '@hh.ru/magritte-ui';

import Captcha from 'src/components/AccountLogin/CombinedLoginCards/ApplicantLoginCard/MultiStepForm/Captcha';
import CodeSender from 'src/components/AccountLogin/CombinedLoginCards/ApplicantLoginCard/MultiStepForm/CodeSender';
import { type ApplicantLoginFormValues } from 'src/components/AccountLogin/CombinedLoginCards/ApplicantLoginCard/MultiStepForm/types';
import {
    CODE_2FA_FIELD,
    isTwoFactorAccountBlockedErrorKey,
    PUSH_CODE_FIELD,
} from 'src/components/AccountLogin/CombinedLoginCards/ApplicantLoginCard/MultiStepForm/utils';
import { useApplicantLoginContext } from 'src/components/AccountLogin/CombinedLoginCards/ApplicantLoginCard/context';
import useTwoFactor from 'src/components/AccountLogin/CombinedLoginCards/ApplicantLoginCard/hooks/useTwoFactor';
import { TwoFactorType } from 'src/models/twoFactor';

import TrustThisSourceField from 'src/components/AccountLogin/CombinedLoginCards/ApplicantLoginCard/MultiStepForm/TwoFactorStep/TrustThisSourceField';
import TwoFactorField from 'src/components/AccountLogin/CombinedLoginCards/ApplicantLoginCard/MultiStepForm/TwoFactorStep/TwoFactorField';
import TwoFactorPushField from 'src/components/AccountLogin/CombinedLoginCards/ApplicantLoginCard/MultiStepForm/TwoFactorStep/TwoFactorPushField';

const CodeLengthMap = {
    [TwoFactorType.Code]: 4,
    [TwoFactorType.Totp]: 6,
};

const TrlKeys = {
    title: 'auth.twoFactor.title',
    subtitle: {
        [TwoFactorType.Code]: 'auth.twoFactor.description',
        [TwoFactorType.Totp]: 'auth.twoFactor.totp.description',
        [TwoFactorType.Push]: 'auth.twoFactor.push.description',
    },
    action: {
        getHelp: 'auth.twoFactor.noAccessEmail.button',
    },
    twoFactorAuthAccountBlockedDescription: 'auth.twoFactor.accountBlocked.description',
    twoFactorAuthAccountBlockedDescriptionSupport: 'auth.twoFactor.accountBlocked.descriptionSupport',
};

const TwoFactorStep: TranslatedComponent = ({ trls }) => {
    const { twoFactor, onNextStep, onStepReplace } = useApplicantLoginContext();

    const { mutators, getState, submit } = useForm<ApplicantLoginFormValues>();
    const { username, password } = getState().values;

    const { resendTwoFactorCode } = useTwoFactor();
    const { resetCaptcha } = useCaptcha();

    const {
        type = TwoFactorType.Code,
        secondsUntilNextSend = 0,
        pushExternalAccessToken = '',
        pushRedirectUrl = '',
    } = twoFactor || {};

    useEffect(() => {
        mutators.removeValue(CODE_2FA_FIELD);

        return () => {
            resetCaptcha();
        };
    }, [mutators, resetCaptcha]);

    const handleFieldChange = useCallback(
        (value: ApplicantLoginFormValues['pushCode']) => {
            mutators.setValue(PUSH_CODE_FIELD, value);
        },
        [mutators]
    );

    const handleSubmit = async (): Promise<void> => {
        const submitErrors = await submit();

        if (isTwoFactorAccountBlockedErrorKey(submitErrors?.code2fa)) {
            onStepReplace('two-factor-account-blocked');
        }
    };

    const handleSendCode = async (): Promise<number | undefined> => {
        const result = await resendTwoFactorCode({
            username,
            password,
            accountType: 'APPLICANT',
        });

        if (result.success) {
            return result.data.waitBeforeSendTime;
        }

        return undefined;
    };

    const handleGetHelpButtonClick = (): void => {
        onNextStep('two-factor-get-help');
    };

    return (
        <VSpacingContainer default={24}>
            <Title
                Element="h2"
                size="medium"
                alignment="center"
                description={trls[TrlKeys.subtitle[type]]}
                data-qa="login-two-factor-title"
            >
                {trls[TrlKeys.title]}
            </Title>
            {type === TwoFactorType.Push ? (
                <TwoFactorPushField
                    pushExternalAccessToken={pushExternalAccessToken}
                    pushRedirectUrl={pushRedirectUrl}
                    onFieldChange={handleFieldChange}
                    onSubmit={handleSubmit}
                />
            ) : (
                <TwoFactorField codeLength={CodeLengthMap[type]} onSubmit={handleSubmit} />
            )}
            <Captcha />
            {type === TwoFactorType.Code ? (
                <VSpacingContainer default={12}>
                    <CodeSender secondsUntilNextSend={secondsUntilNextSend} onSend={handleSendCode} />
                    <Button
                        size="large"
                        style="accent"
                        mode="tertiary"
                        data-qa="account-login-no-access-email"
                        stretched
                        onClick={handleGetHelpButtonClick}
                    >
                        {trls[TrlKeys.action.getHelp]}
                    </Button>
                </VSpacingContainer>
            ) : (
                <TrustThisSourceField />
            )}
        </VSpacingContainer>
    );
};

export default translation(TwoFactorStep);
